import React, { FC } from 'react';
import { DATE_FORMAT } from 'web/src/config/constants';
import { formatDate } from 'web/src/utils/date-format';
import { Icon, Typography } from '../shared';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useBmisState } from '@packages/contexts/bmis';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

const styles = StyleSheet.create({
    section: {
        paddingHorizontal: 24,
        paddingVertical: 12,
    },
    labelWrapper: {
        alignItems: 'baseline',
        flexDirection: 'row',
    },
    title: {
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 24,
    },
    subTitle: {
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 24,
        marginTop: 8,
        textTransform: 'uppercase',
    },
    content: {
        fontWeight: '400',
        lineHeight: 24,
    },
});

interface DetailsCardProps {
    content?: string;
    subTitle?: string;
    title: string;
}

export const DetailsCard: FC<DetailsCardProps> = ({ title, subTitle, content }) => (
    <View style={styles.section}>
        {title && (
            <View style={styles.labelWrapper}>
                <Typography variant="h6" style={styles.title}>
                    {title}
                </Typography>
            </View>
        )}

        {subTitle && (
            <Typography style={styles.subTitle} color="blueOne">
                {subTitle}
            </Typography>
        )}
        {content && <Typography style={styles.content}>{content}</Typography>}
    </View>
);

export const getRetailerLocation = (retailer?: { districtCode?: string; regionCode?: string; zoneCode?: string }) => {
    if (!retailer) return 'N/A';
    const { regionCode = '', zoneCode = '', districtCode = '' } = retailer;

    return `Region ${regionCode}, Zone ${zoneCode}, District ${districtCode}`;
};

export const BmisAsideDetails: FC = () => {
    const { t } = useTranslation();
    const { selectedBmisRow } = useBmisState();

    const { retailer, serviceDateandTime, techID } = selectedBmisRow || {};
    const formattedServiceDate = serviceDateandTime
        ? formatDate(serviceDateandTime, DATE_FORMAT.MON_DAY_YEAR_AT_H_MM)
        : 'N/A';

    const retailerLocation = getRetailerLocation(retailer);

    return (
        <ScrollView>
            <DetailsCard
                title={t('Retailer')}
                subTitle={retailer ? `${retailer.name} (#${retailer.code})` : 'N/A'}
                content={retailerLocation}
            />
            <DetailsCard title={t('Tested By')} subTitle={techID || 'N/A'} content={formattedServiceDate} />
        </ScrollView>
    );
};

const noDetailsStyles = StyleSheet.create({
    container: {
        alignItems: 'center',
        display: 'flex',
        gap: 24,
        justifyContent: 'center',
        margin: 24,
    },
    iconContainer: {
        backgroundColor: colors.grayTwo,
        borderRadius: 240,
        gap: 10,
        height: 160,
        padding: 40,
        width: 160,
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
    },
    textContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 0,
        justifyContent: 'center',
        marginTop: 0,
        width: '100%',
    },
    text: {
        fontSize: 21,
        fontWeight: '600',
        lineHeight: 32,
    },
});

export const NoBmisAsideDetails: FC = () => {
    return (
        <View style={noDetailsStyles.container}>
            <View style={noDetailsStyles.iconContainer}>
                <Icon name="car-lift" size={66.67} color="grayFive" />
            </View>
            <View style={noDetailsStyles.textContainer}>
                <Typography style={noDetailsStyles.text} color="textDarkSecondary">
                    Retailer information
                </Typography>
                <Typography style={noDetailsStyles.text} color="textDarkSecondary">
                    is not available
                </Typography>
            </View>
        </View>
    );
};
